<template>
    <div class="location-worksites-page">
        <div class="worksites-shell" :class="{ 'is-editor-open': isModalActive }">
            <aside class="worksites-sidebar" :class="{ 'is-editor-open': isModalActive }">
                <div class="sidebar-hero">
                    <div>
                        <p class="sidebar-kicker">{{ trans('location_worksites_kicker', 'Mozali Worksites') }}</p>
                        <h2>{{ $t('locations') }}</h2>
                        <p class="sidebar-copy">
                            {{ trans('location_worksites_intro', 'Manage worksites, geofences, and operational rules from one view.') }}
                        </p>
                    </div>
                    <button type="button" class="btn btn-primary btn-with-shadow sidebar-add-btn" @click="openCreateModal">
                        <app-icon name="plus" class="mr-2" />
                        {{ trans('add_location', 'Add Location') }}
                    </button>
                </div>

                <div class="sidebar-filters card">
                    <div class="filter-row">
                        <label class="filter-label" for="locationSearch">{{ $t('search') }}</label>
                        <input
                            id="locationSearch"
                            v-model.trim="searchQuery"
                            type="text"
                            class="form-control"
                            :placeholder="trans('location_search_placeholder', 'Name, code, address')"
                        />
                    </div>
                    <div class="filter-row mb-0">
                        <label class="filter-label" for="locationStatusFilter">{{ $t('status') }}</label>
                        <select id="locationStatusFilter" v-model="statusFilter" class="form-control">
                            <option value="all">{{ $t('all') }}</option>
                            <option value="active">{{ $t('active') }}</option>
                            <option value="inactive">{{ $t('inactive') }}</option>
                        </select>
                    </div>
                    <div class="filter-pills">
                        <span class="filter-pill active-pill">{{ trans('location_count_active', ':count active', { count: activeCount }) }}</span>
                        <span class="filter-pill inactive-pill">{{ trans('location_count_inactive', ':count inactive', { count: inactiveCount }) }}</span>
                        <span class="filter-pill neutral-pill">{{ trans('location_count_visible', ':count visible', { count: visibleLocations.length }) }}</span>
                    </div>
                </div>

                <div class="sidebar-list card">
                    <div class="sidebar-list-head">
                        <h5>{{ $t('locations') }}</h5>
                        <span>{{ visibleLocations.length }}</span>
                    </div>

                    <div class="sidebar-list-body">
                        <div v-if="isLoading" class="list-state">
                            <app-overlay-loader />
                        </div>

                        <div v-else-if="!visibleLocations.length" class="list-state empty-state">
                            <app-icon name="map-pin" />
                            <p>{{ trans('no_locations_match_filters', 'No locations match the current filters.') }}</p>
                        </div>

                        <template v-else>
                            <button
                                v-for="location in visibleLocations"
                                :key="location.id"
                                type="button"
                                class="location-list-item"
                                :class="{ 'is-selected': selectedLocation && selectedLocation.id === location.id }"
                                @click="selectLocation(location)"
                            >
                                <div class="location-item-main">
                                    <div>
                                        <div class="location-item-title-row">
                                            <h6>{{ location.name }}</h6>
                                            <span class="status-dot" :class="location.is_active ? 'dot-active' : 'dot-inactive'"></span>
                                        </div>
                                        <p>{{ location.address || location.description || trans('no_address_specified', 'No address specified') }}</p>
                                    </div>
                                    <span class="geofence-chip" :class="`type-${location.geofence_type}`">
                                        {{ geofenceTypeLabel(location.geofence_type) }}
                                    </span>
                                </div>
                                <div class="location-item-meta">
                                    <span>{{ location.code || trans('no_code', 'No code') }}</span>
                                    <span v-if="location.geofence_type === 'radius'">{{ location.radius_m }} m</span>
                                    <span v-else>{{ trans('location_polygon_points', ':count pts', { count: polygonPointCount(location.geojson) }) }}</span>
                                </div>
                            </button>
                        </template>
                    </div>
                </div>

                <div v-if="selectedLocation" class="selected-card card">
                    <div class="selected-card-head">
                        <div>
                            <p class="detail-kicker">{{ trans('selected', 'Selected') }}</p>
                            <h4>{{ selectedLocation.name }}</h4>
                        </div>
                        <span class="selected-status" :class="selectedLocation.is_active ? 'is-active' : 'is-inactive'">
                            {{ selectedLocation.is_active ? $t('active') : $t('inactive') }}
                        </span>
                    </div>

                    <p class="selected-address">{{ selectedLocation.address || selectedLocation.description || trans('no_address_available', 'No address available.') }}</p>

                    <div class="selected-grid">
                        <div>
                            <span class="selected-label">{{ trans('geofence', 'Geofence') }}</span>
                            <strong>{{ geofenceTypeLabel(selectedLocation.geofence_type) }}</strong>
                        </div>
                        <div>
                            <span class="selected-label">{{ $t('time_zone') }}</span>
                            <strong>{{ selectedLocation.time_zone || trans('not_available', 'Not available') }}</strong>
                        </div>
                        <div>
                            <span class="selected-label">{{ trans('clockin_distance', 'Clock-in distance') }}</span>
                            <strong>{{ selectedLocation.allowed_clockin_distance_m }} m</strong>
                        </div>
                        <div>
                            <span class="selected-label">{{ trans('gps_tolerance', 'GPS tolerance') }}</span>
                            <strong>{{ selectedLocation.gps_tolerance_m }} m</strong>
                        </div>
                        <div>
                            <span class="selected-label">{{ trans('offline_attendance', 'Offline attendance') }}</span>
                            <strong>{{ selectedLocation.offline_attendance_allowed ? trans('allowed', 'Allowed') : $t('disabled') }}</strong>
                        </div>
                        <div>
                            <span class="selected-label">{{ trans('location_active_label', 'Location active') }}</span>
                            <strong>{{ selectedLocation.is_active ? trans('enabled', 'Enabled') : $t('disabled') }}</strong>
                        </div>
                    </div>

                    <div class="selected-actions">
                        <button type="button" class="btn btn-outline-primary" @click="openEditModal(selectedLocation)">
                            {{ $t('edit') }}
                        </button>
                        <button type="button" class="btn btn-outline-danger" @click="openDeleteConfirmation(selectedLocation)">
                            {{ $t('delete') }}
                        </button>
                    </div>
                </div>
            </aside>

            <section class="worksites-stage card" :class="{ 'is-editor-open': isModalActive, 'is-editor-collapsed': isModalActive && isEditorCollapsed }">
                <div class="stage-toolbar">
                    <div>
                        <!-- <p class="toolbar-kicker">{{ trans('google_maps', 'Google Maps') }}</p> -->
                        <h5>{{ selectedLocation ? selectedLocation.name : trans('location_coverage_overview', 'Location coverage overview') }}</h5>
                    </div>
                    <div class="toolbar-actions">
                        <button type="button" class="btn btn-outline-primary" @click="reloadLocations">
                            <app-icon name="refresh-cw" class="mr-2" />
                            {{ trans('refresh', 'Refresh') }}
                        </button>
                        <button v-if="selectedLocation" type="button" class="btn btn-primary" @click="focusLocationOnMap(selectedLocation)">
                            {{ trans('focus_map', 'Focus map') }}
                        </button>
                    </div>
                </div>

                <div class="stage-map-wrap">
                    <div ref="map" class="worksites-map"></div>

                    <!-- <div v-if="isModalActive" class="map-overlay editor-status-overlay">
                        <div class="editor-status-copy">
                            <p class="detail-kicker mb-1">{{ trans('editing_worksite', 'Editing worksite') }}</p>
                            <strong>{{ modalTitle }}</strong>
                            <span>{{ trans('full_screen_workspace_hint', 'The editor now uses a full-screen workspace so the map stays visible while you configure the worksite.') }}</span>
                        </div>
                        <div class="editor-status-actions">
                            <button type="button" class="btn btn-primary btn-sm" @click="saveLocation">
                                {{ isEditing ? trans('update', 'Update') : trans('save', 'Save') }}
                            </button>
                            <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeModal">
                                {{ $t('cancel') }}
                            </button>
                        </div>
                    </div> -->

                    <div class="map-overlay map-mode-overlay">
                        <div class="btn-group btn-group-sm map-mode-toggle" role="group" :aria-label="trans('map_mode', 'Map mode')">
                            <button
                                type="button"
                                class="btn map-mode-btn"
                                :class="{ 'is-active': mapViewMode === 'roadmap' }"
                                @click="setMapViewMode('roadmap')"
                            >
                                <app-icon name="map" class="map-mode-btn-icon" />
                                <span>{{ trans('map_standard', 'Standard') }}</span>
                            </button>
                            <button
                                type="button"
                                class="btn map-mode-btn"
                                :class="{ 'is-active': mapViewMode === 'satellite' }"
                                @click="setMapViewMode('satellite')"
                            >
                                <app-icon name="image" class="map-mode-btn-icon" />
                                <span>{{ trans('map_satellite', 'Satellite') }}</span>
                            </button>
                            <button
                                type="button"
                                class="btn map-mode-btn"
                                :class="{ 'is-active': mapViewMode === 'hybrid' }"
                                @click="setMapViewMode('hybrid')"
                            >
                                <app-icon name="layers" class="map-mode-btn-icon" />
                                <span>{{ trans('map_hybrid', 'Hybrid') }}</span>
                            </button>
                        </div>
                    </div>

                    <div class="map-overlay legend-overlay">
                        <div class="legend-row">
                            <span class="legend-swatch swatch-active"></span>
                            <span>{{ trans('legend_location_active', 'Location active') }}</span>
                        </div>
                        <div class="legend-row">
                            <span class="legend-swatch swatch-inactive"></span>
                            <span>{{ trans('legend_location_inactive', 'Location inactive') }}</span>
                        </div>
                        <div class="legend-row">
                            <span class="legend-swatch swatch-selected"></span>
                            <span>{{ trans('legend_current_selection', 'Current selection') }}</span>
                        </div>
                    </div>

                    
                    <div v-if="isModalActive" class="map-overlay geofence-studio-overlay">
                        <div class="studio-header">
                            <div>
                                <p class="detail-kicker mb-1">{{ trans('geofence_studio', 'Mozali Geofence studio') }}</p>
                                <h5 class="mb-0">{{ geofenceTypeLabel(form.geofence_type) }}</h5>
                            </div>
                            <span class="editor-badge">{{ trans('map_first', 'Map-first') }}</span>
                        </div>

                        <div class="type-toggle geofence-overlay-toggle">
                            <button
                                type="button"
                                class="type-toggle-btn"
                                :class="{ active: form.geofence_type === 'radius' }"
                                @click="setGeofenceType('radius')"
                            >
                                {{ trans('radius', 'Radius') }}
                            </button>
                            <button
                                type="button"
                                class="type-toggle-btn"
                                :class="{ active: form.geofence_type === 'polygon' }"
                                @click="setGeofenceType('polygon')"
                            >
                                {{ trans('polygon', 'Polygon') }}
                            </button>
                        </div>

                        <div v-if="form.geofence_type === 'radius'" class="studio-panel">
                            <div class="studio-coordinates">
                                <div>
                                    <span>{{ trans('center_lat', 'Center lat') }}</span>
                                    <strong>{{ displayCoordinate(form.center_lat) }}</strong>
                                </div>
                                <div>
                                    <span>{{ trans('center_lng', 'Center lng') }}</span>
                                    <strong>{{ displayCoordinate(form.center_lng) }}</strong>
                                </div>
                            </div>
                            <p class="studio-copy">{{ trans('radius_map_instruction', 'Click on the map or drag the marker to place the worksite center.') }}</p>
                            <div class="form-group mb-0">
                                <div class="radius-header">
                                    <label for="radiusRangeOverlay">{{ trans('radius', 'Radius') }}</label>
                                    <strong>{{ form.radius_m }} m</strong>
                                </div>
                                <input id="radiusRangeOverlay" v-model.number="form.radius_m" type="range" class="custom-range" min="25" max="2000" step="25" />
                            </div>
                        </div>

                        <div v-else class="studio-panel">
                            <div class="polygon-guidance compact">
                                <strong>{{ polygonFlowTitle() }}</strong>
                                <p>{{ polygonFlowDescription() }}</p>
                            </div>
                            <div class="polygon-toolbar">
                                <button
                                    type="button"
                                    class="btn btn-outline-primary btn-sm"
                                    :disabled="isDrawingPolygon"
                                    @click="beginPolygonDrawing"
                                >
                                    {{ polygonActionLabel() }}
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-outline-secondary btn-sm"
                                    :disabled="!form.geojson && !isDrawingPolygon"
                                    @click="clearPolygonDrawing"
                                >
                                    {{ trans('clear_polygon', 'Clear polygon') }}
                                </button>
                            </div>
                            <div class="polygon-summary compact">
                                <span>{{ trans('polygon_points', ':count points', { count: polygonPointCount(form.geojson) }) }}</span>
                                <span v-if="form.center_lat && form.center_lng">
                                    {{ trans('center_coordinates', 'Center :lat, :lng', { lat: displayCoordinate(form.center_lat), lng: displayCoordinate(form.center_lng) }) }}
                                </span>
                            </div>
                        </div>
                        <p v-if="isModalActive" class="text-muted">
                            <template v-if="form.geofence_type === 'radius'">
                                {{ trans('map_radius_help', 'Click on the map to place the center of the radius, or drag the preview marker.') }}
                            </template>
                            <template v-else>
                                {{ trans('map_polygon_help', 'Draw a polygon on the map, then adjust its points directly in Google Maps.') }}
                            </template>
                        </p>
                    </div>
                </div>
            </section>
        </div>

        <transition name="editor-drawer">
            <div v-if="isModalActive" class="editor-drawer-shell">
                <div class="location-editor-drawer" :class="{ 'is-collapsed': isEditorCollapsed }">
                    <div v-if="isEditorCollapsed" class="drawer-toggle-rail">
                        <button
                            type="button"
                            class="drawer-toggle-btn"
                            :title="trans('expand_worksite_editor', 'Expand worksite editor')"
                            @click="toggleEditorCollapse"
                        >
                            <app-icon name="chevron-left" />
                        </button>
                        <button
                            type="button"
                            class="drawer-toggle-btn"
                            :title="$t('close')"
                            @click.prevent="closeModal"
                        >
                            <app-icon name="x" />
                        </button>
                    </div>

                    <div class="drawer-panel" :class="{ 'is-collapsed': isEditorCollapsed }">
                        <div class="drawer-header">
                            <div>
                                <p class="detail-kicker mb-1">{{ trans('worksite_editor', 'Worksite editor') }}</p>
                                <h5 class="modal-title mb-0">{{ modalTitle }}</h5>
                            </div>
                            <div class="drawer-header-actions">
                                <button
                                    type="button"
                                    class="drawer-toggle-btn"
                                    :title="trans('collapse_worksite_editor', 'Collapse worksite editor')"
                                    @click="toggleEditorCollapse"
                                >
                                    <app-icon name="chevron-right" />
                                </button>
                                <button type="button" class="close outline-none" @click.prevent="closeModal">
                                    <span><app-icon name="x" /></span>
                                </button>
                            </div>
                        </div>

                        <div class="drawer-body custom-scrollbar">
                            <app-overlay-loader v-if="isSaving" />
                            <div class="location-editor" :class="{ 'loading-opacity': isSaving }">
                                <form class="mb-0" @submit.prevent="saveLocation">
                                    <div class="editor-grid">
                                        <div class="editor-section card">
                                            <div class="editor-section-head">
                                                <div>
                                                    <p class="detail-kicker">{{ trans('worksite', 'Worksite') }}</p>
                                                    <h5>{{ trans('location_details', 'Location details') }}</h5>
                                                </div>
                                                <span class="editor-badge">{{ geofenceTypeLabel(form.geofence_type) }}</span>
                                            </div>

                                            <div class="form-group">
                                                <label for="locationName">{{ $t('name') }}</label>
                                                <input id="locationName" v-model.trim="form.name" type="text" class="form-control" :placeholder="trans('location_name_placeholder', 'Main Office, Plant A...')" />
                                            </div>

                                            <div class="form-group">
                                                <label for="locationCode">{{ $t('code') }}</label>
                                                <input id="locationCode" v-model.trim="form.code" type="text" class="form-control" :placeholder="trans('optional', 'Optional')" />
                                            </div>

                                            <div class="form-group">
                                                <label for="locationAddress">{{ $t('address') }}</label>
                                                <input id="locationAddress" v-model.trim="form.address" type="text" class="form-control" :placeholder="trans('location_address_placeholder', 'Address or operational landmark')" />
                                            </div>

                                            <div class="form-group">
                                                <label for="locationDescription">{{ $t('description') }}</label>
                                                <textarea id="locationDescription" v-model.trim="form.description" class="form-control" rows="3" :placeholder="trans('location_description_placeholder', 'Additional operational details')"></textarea>
                                            </div>

                                            <div class="coordinates-card">
                                                <div class="coordinates-card__head">
                                                    <div>
                                                        <strong>{{ trans('map_coordinates', 'Map coordinates') }}</strong>
                                                        <p>{{ trans('location_coordinate_help', 'Click on the map or enter coordinates manually. Both options update the same worksite position.') }}</p>
                                                    </div>
                                                </div>

                                                <div class="settings-grid">
                                                    <div class="form-group mb-0">
                                                        <label for="locationLatitude">{{ $t('latitude') }}</label>
                                                        <input
                                                            id="locationLatitude"
                                                            v-model.number="form.center_lat"
                                                            type="number"
                                                            step="0.000001"
                                                            min="-90"
                                                            max="90"
                                                            class="form-control"
                                                            @change="applyManualCoordinates"
                                                        />
                                                    </div>
                                                    <div class="form-group mb-0">
                                                        <label for="locationLongitude">{{ $t('longitude') }}</label>
                                                        <input
                                                            id="locationLongitude"
                                                            v-model.number="form.center_lng"
                                                            type="number"
                                                            step="0.000001"
                                                            min="-180"
                                                            max="180"
                                                            class="form-control"
                                                            @change="applyManualCoordinates"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="form-group mb-0">
                                                <label for="locationTimeZone">{{ $t('time_zone') }}</label>
                                                <app-input
                                                    :key="`location-time-zone-${timeZones.length}-${isModalActive ? 'open' : 'closed'}`"
                                                    id="locationTimeZone"
                                                    v-model="form.time_zone"
                                                    :list="timeZones"
                                                    type="search-select"
                                                />
                                            </div>
                                        </div>

                                        <div class="editor-section card">
                                            <div class="editor-section-head">
                                                <div>
                                                    <p class="detail-kicker">{{ trans('attendance_rules', 'Attendance rules') }}</p>
                                                    <h5>{{ trans('shape_and_rules', 'Shape and rules') }}</h5>
                                                </div>
                                            </div>

                                            <div class="map-studio-summary">
                                                <strong>{{ trans('geofence', 'Geofence') }}</strong>
                                                <p>{{ trans('map_studio_summary', 'Use the map studio to place the center, adjust the radius, or draw and refine polygons directly on the map.') }}</p>
                                                <div class="polygon-summary compact mb-0">
                                                    <span>{{ geofenceTypeLabel(form.geofence_type) }}</span>
                                                    <span v-if="form.geofence_type === 'radius'">{{ form.radius_m }} m</span>
                                                    <span v-else>{{ trans('polygon_points', ':count points', { count: polygonPointCount(form.geojson) }) }}</span>
                                                </div>
                                            </div>

                                            <div class="settings-grid">
                                                <div class="form-group">
                                                    <label for="gpsTolerance">{{ trans('gps_tolerance_m_label', 'GPS tolerance (m)') }}</label>
                                                    <input id="gpsTolerance" v-model.number="form.gps_tolerance_m" type="number" min="0" class="form-control" />
                                                </div>
                                                <div class="form-group">
                                                    <label for="clockinDistance">{{ trans('allowed_clockin_distance_label', 'Allowed clock-in distance (m)') }}</label>
                                                    <input id="clockinDistance" v-model.number="form.allowed_clockin_distance_m" type="number" min="0" class="form-control" />
                                                </div>
                                            </div>

                                            <div class="switch-row">
                                                <div>
                                                    <strong>{{ trans('offline_attendance', 'Offline attendance') }}</strong>
                                                    <p>{{ trans('offline_attendance_help', 'Allow offline attendance punches for this worksite.') }}</p>
                                                </div>
                                                <button
                                                    id="offlineAttendanceAllowed"
                                                    type="button"
                                                    class="toggle-switch"
                                                    :class="{ 'is-on': form.offline_attendance_allowed }"
                                                    :aria-pressed="`${!!form.offline_attendance_allowed}`"
                                                    @click="form.offline_attendance_allowed = !form.offline_attendance_allowed"
                                                >
                                                    <span class="toggle-switch-track">
                                                        <span class="toggle-switch-thumb"></span>
                                                    </span>
                                                    <span class="toggle-switch-text">
                                                        {{ form.offline_attendance_allowed ? trans('enabled', 'Enabled') : $t('disabled') }}
                                                    </span>
                                                </button>
                                            </div>

                                            <div class="switch-row mb-0">
                                                <div>
                                                    <strong>{{ trans('location_active_label', 'Location active') }}</strong>
                                                    <p>{{ trans('location_active_help', 'Include this location in lists and attendance flows.') }}</p>
                                                </div>
                                                <button
                                                    id="locationIsActive"
                                                    type="button"
                                                    class="toggle-switch"
                                                    :class="{ 'is-on': form.is_active }"
                                                    :aria-pressed="`${!!form.is_active}`"
                                                    @click="form.is_active = !form.is_active"
                                                >
                                                    <span class="toggle-switch-track">
                                                        <span class="toggle-switch-thumb"></span>
                                                    </span>
                                                    <span class="toggle-switch-text">
                                                        {{ form.is_active ? trans('enabled', 'Enabled') : $t('disabled') }}
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                </form>
                            </div>
                        </div>

                        <div class="drawer-footer">
                            <button type="button" class="btn btn-primary mr-2" @click="saveLocation">
                                {{ isEditing ? trans('update_location', 'Update location') : trans('save_location', 'Save location') }}
                            </button>
                            <button type="button" class="btn btn-secondary mr-2" @click="closeModal">
                                {{ $t('cancel') }}
                            </button>
                            <button v-if="isEditing" type="button" class="btn btn-outline-danger" @click="openDeleteConfirmation(selectedLocation)">
                                {{ $t('delete') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <app-delete-modal
            v-if="deleteConfirmationModalActive"
            :preloader="deleteLoader"
            modal-id="location-delete"
            @confirmed="confirmedDelete"
            @cancelled="cancelDelete"
        />
    </div>
</template>

<script>
import * as actions from '../../../Config/ApiUrl';
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import { FormMixin } from '../../../../core/mixins/form/FormMixin';

const DEFAULT_CENTER = { lat: -3.8626, lng: 23.4878 };
const DEFAULT_RADIUS = 150;
const FALLBACK_GOOGLE_MAPS_KEY = 'AIzaSyA8emh2Pq3wIootXyoX5zILgIqaxxOrs-E';
const FOCUS_PADDING = 96;
const MAX_FOCUS_ZOOM = 16;
const ACTIVE_LOCATION_COLOR = '#16a34a';
const INACTIVE_LOCATION_COLOR = '#dc2626';

function createWorksiteMarkerOverlay({ google, map, location, isSelected, onClick }) {
    class WorksiteMarkerOverlay extends google.maps.OverlayView {
        constructor() {
            super();
            this.location = location;
            this.position = new google.maps.LatLng(location.center_lat, location.center_lng);
            this.isSelected = isSelected;
            this.onClick = onClick;
            this.container = null;
            this.button = null;
            this.clickHandler = null;
        }

        onAdd() {
            const container = document.createElement('div');
            container.className = 'worksite-marker-overlay';

            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'worksite-marker-button';
            button.setAttribute('aria-label', this.location.name);

            const badge = document.createElement('span');
            badge.className = 'worksite-marker-badge';

            const badgeDot = document.createElement('span');
            badgeDot.className = 'worksite-marker-badge-dot';

            const badgeText = document.createElement('span');
            badgeText.className = 'worksite-marker-badge-text';
            badgeText.textContent = this.location.name;

            badge.appendChild(badgeDot);
            badge.appendChild(badgeText);

            const pinWrap = document.createElement('span');
            pinWrap.className = 'worksite-marker-pin-wrap';

            if (this.location.is_active) {
                const pulse = document.createElement('span');
                pulse.className = 'worksite-marker-pulse';
                pinWrap.appendChild(pulse);
            }

            const pin = document.createElement('span');
            pin.className = 'worksite-marker-pin';
            pinWrap.appendChild(pin);

            button.appendChild(badge);
            button.appendChild(pinWrap);
            container.appendChild(button);

            this.clickHandler = event => {
                event.preventDefault();
                event.stopPropagation();

                if (typeof this.onClick === 'function') {
                    this.onClick(this.location);
                }
            };

            button.addEventListener('click', this.clickHandler);

            this.container = container;
            this.button = button;

            this.applyStateClasses();
            this.getPanes().overlayMouseTarget.appendChild(container);
        }

        draw() {
            if (!this.container) {
                return;
            }

            const projection = this.getProjection();

            if (!projection) {
                return;
            }

            const pixel = projection.fromLatLngToDivPixel(this.position);

            if (!pixel) {
                return;
            }

            this.container.style.left = `${pixel.x}px`;
            this.container.style.top = `${pixel.y}px`;
            this.container.style.zIndex = this.isSelected ? '1200' : (this.location.is_active ? '1100' : '1000');
        }

        onRemove() {
            if (this.button && this.clickHandler) {
                this.button.removeEventListener('click', this.clickHandler);
            }

            if (this.container?.parentNode) {
                this.container.parentNode.removeChild(this.container);
            }

            this.container = null;
            this.button = null;
            this.clickHandler = null;
        }

        applyStateClasses() {
            if (!this.container) {
                return;
            }

            this.container.classList.toggle('is-active', !!this.location.is_active);
            this.container.classList.toggle('is-inactive', !this.location.is_active);
            this.container.classList.toggle('is-selected', !!this.isSelected);
        }

        updateSelection(isSelected) {
            this.isSelected = isSelected;
            this.applyStateClasses();
            this.draw();
        }
    }

    const overlay = new WorksiteMarkerOverlay();
    overlay.setMap(map);

    return overlay;
}

export default {
    name: 'AppLocations',
    extends: CoreLibrary,
    mixins: [FormMixin],
    data() {
        return {
            map: null,
            infoWindow: null,
            drawingManager: null,
            geocoder: null,
            locationLayers: {},
            editingMarker: null,
            editingCircle: null,
            editingPolygon: null,
            deleteLoader: false,
            deleteConfirmationModalActive: false,
            isDrawingPolygon: false,
            isLoading: false,
            isSaving: false,
            isMapReady: false,
            isModalActive: false,
            isEditorCollapsed: false,
            mapViewMode: 'roadmap',
            locations: [],
            placeAutocomplete: null,
            placeSearchMarker: null,
            pendingDeleteLocationId: null,
            prefillRequestToken: 0,
            searchQuery: '',
            selectedLocationId: null,
            statusFilter: 'all',
            timeZones: [],
            form: this.defaultForm(),
        };
    },
    computed: {
        selectedLocation() {
            return this.locations.find(location => `${location.id}` === `${this.selectedLocationId}`) || null;
        },
        activeCount() {
            return this.locations.filter(location => !!location.is_active).length;
        },
        inactiveCount() {
            return this.locations.filter(location => !location.is_active).length;
        },
        visibleLocations() {
            const query = this.searchQuery.trim().toLowerCase();

            return this.locations.filter(location => {
                const matchesStatus = this.statusFilter === 'all'
                    ? true
                    : this.statusFilter === 'active'
                        ? !!location.is_active
                        : !location.is_active;

                if (!matchesStatus) {
                    return false;
                }

                if (!query) {
                    return true;
                }

                return [location.name, location.code, location.address, location.description]
                    .filter(Boolean)
                    .some(value => value.toLowerCase().includes(query));
            });
        },
        isEditing() {
            return !!this.form.id;
        },
        modalTitle() {
            return this.isEditing
                ? `${this.$t('edit')} ${this.form.name || this.$t('location')}`
                : this.trans('add_location', 'Add Location');
        },
        googleMapsApiKey() {
            return process.env.MIX_GOOGLE_MAPS_API_KEY || FALLBACK_GOOGLE_MAPS_KEY;
        },
    },
    watch: {
        visibleLocations: {
            handler() {
                this.renderMapLayers();
            },
            deep: true,
        },
        'form.geofence_type'(value) {
            if (!this.isModalActive || !this.isMapReady) {
                return;
            }

            this.handleGeofenceModeChange(value);
        },
        'form.radius_m'() {
            if (this.isModalActive && this.form.geofence_type === 'radius') {
                this.syncRadiusPreview();
            }
        },
    },
    mounted() {
        this.bootstrapPage();
    },
    beforeDestroy() {
        this.clearMapLayers();
        this.clearEditingOverlays();

        if (this.drawingManager) {
            this.drawingManager.setMap(null);
            this.drawingManager = null;
        }

        if (this.placeAutocomplete && window.google?.maps) {
            window.google.maps.event.clearInstanceListeners(this.placeAutocomplete);
            this.placeAutocomplete = null;
        }

        if (this.placeSearchMarker) {
            this.placeSearchMarker.setMap(null);
            this.placeSearchMarker = null;
        }
    },
    methods: {
        trans(key, fallback = null, params = {}) {
            const translated = this.$t(key, params);
            const resolvedMessage = translated !== key
                ? translated
                : (fallback === null ? translated : fallback);

            if (!Object.keys(params).length) {
                return resolvedMessage;
            }

            return Object.keys(params).reduce((message, parameter) => {
                return message.replace(new RegExp(`:${parameter}`, 'g'), params[parameter]);
            }, resolvedMessage);
        },
        toastSuccess(key, fallback = null, params = {}) {
            this.$toastr.s(this.trans(key, fallback, params));
        },
        toastError(key, fallback = null, params = {}) {
            this.$toastr.e(this.trans(key, fallback, params));
        },
        toastInfo(key, fallback = null, params = {}) {
            this.$toastr.i(this.trans(key, fallback, params));
        },
        geofenceTypeLabel(type) {
            return this.trans(type === 'polygon' ? 'polygon' : 'radius', type === 'polygon' ? 'Polygon' : 'Radius');
        },
        polygonActionLabel() {
            if (this.isDrawingPolygon) {
                return this.trans('polygon_drawing_in_progress', 'Drawing in progress...');
            }

            if (this.form.geojson) {
                return this.trans('redraw_polygon', 'Redraw polygon');
            }

            return this.trans('draw_polygon', 'Draw polygon');
        },
        polygonFlowTitle() {
            if (this.isDrawingPolygon) {
                return this.trans('polygon_flow_title_drawing', 'Drawing polygon');
            }

            if (this.form.geojson) {
                return this.trans('polygon_flow_title_ready', 'Polygon ready');
            }

            return this.trans('polygon_flow_title_start', 'How to draw');
        },
        polygonFlowDescription() {
            if (this.isDrawingPolygon) {
                return this.trans(
                    'polygon_flow_description_drawing',
                    'Click each point on the map, then double-click the last point to finish the polygon.'
                );
            }

            if (this.form.geojson) {
                return this.trans(
                    'polygon_flow_description_ready',
                    'The polygon is on the map. Drag its points to refine the geofence, or redraw it if needed.'
                );
            }

            return this.trans(
                'polygon_flow_description_start',
                'Click "Draw polygon", then click points on the map. Double-click the last point to finish.'
            );
        },
        defaultForm() {
            return {
                id: null,
                name: '',
                code: '',
                address: '',
                description: '',
                time_zone: '',
                geofence_type: 'radius',
                center_lat: null,
                center_lng: null,
                radius_m: DEFAULT_RADIUS,
                geojson: null,
                gps_tolerance_m: 15,
                allowed_clockin_distance_m: 15,
                offline_attendance_allowed: true,
                is_active: true,
            };
        },
        async bootstrapPage() {
            this.isLoading = true;

            try {
                await this.loadGoogleMaps();
                this.initMap();
                await Promise.all([this.loadLocations(), this.getTimeZones()]);
            } catch (error) {
                this.toastError('google_maps_failed_to_load', 'Google Maps failed to load.');
            } finally {
                this.isLoading = false;
            }
        },
        loadGoogleMaps() {
            if (window.google && window.google.maps && window.google.maps.drawing) {
                return Promise.resolve(window.google.maps);
            }

            if (window.__mozaliGoogleMapsPromise) {
                return window.__mozaliGoogleMapsPromise;
            }

            window.__mozaliGoogleMapsPromise = new Promise((resolve, reject) => {
                const existingScript = document.getElementById('mozali-google-maps');

                if (existingScript) {
                    existingScript.addEventListener('load', () => resolve(window.google.maps));
                    existingScript.addEventListener('error', reject);
                    return;
                }

                const script = document.createElement('script');
                script.id = 'mozali-google-maps';
                script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}&libraries=places,drawing,geometry`;
                script.async = true;
                script.defer = true;
                script.onload = () => resolve(window.google.maps);
                script.onerror = reject;
                document.head.appendChild(script);
            });

            return window.__mozaliGoogleMapsPromise;
        },
        initMap() {
            this.map = new window.google.maps.Map(this.$refs.map, {
                center: DEFAULT_CENTER,
                zoom: 6,
                mapTypeControl: false,
                mapTypeId: this.mapViewMode,
                streetViewControl: false,
                fullscreenControl: true,
                styles: [
                    {
                        featureType: 'poi',
                        stylers: [{ visibility: 'off' }],
                    },
                ],
            });

            this.geocoder = new window.google.maps.Geocoder();
            this.initializeDrawingManager();
            // Internal Google Maps place search is temporarily disabled
            // to keep the worksite flow focused on selection and geofence editing.
            this.map.addListener('click', this.handleMapClick);
            this.isMapReady = true;
        },
        initializePlaceSearch() {
            return;
        },
        handlePlaceChanged() {
            if (!this.placeAutocomplete) {
                return;
            }

            const place = this.placeAutocomplete.getPlace();

            if (!place?.geometry?.location) {
                this.toastError('map_search_no_result', 'No precise place was found for this search.');
                return;
            }

            this.mapSearchQuery = place.formatted_address || place.name || '';

            if (place.geometry.viewport) {
                this.fitMapToBounds(place.geometry.viewport, FOCUS_PADDING, false);
            } else {
                this.map.setCenter(place.geometry.location);
                this.map.setZoom(16);
            }

            this.setPlaceSearchMarker(place.geometry.location, place.name || place.formatted_address || '');
        },
        setPlaceSearchMarker(position, title = '') {
            if (!position) {
                return;
            }

            if (!this.placeSearchMarker) {
                this.placeSearchMarker = new window.google.maps.Marker({
                    map: this.map,
                    zIndex: 1200,
                    animation: window.google.maps.Animation.DROP,
                });
            }

            this.placeSearchMarker.setPosition(position);
            this.placeSearchMarker.setTitle(title);
            this.placeSearchMarker.setMap(this.map);
        },
        setMapViewMode(mode) {
            if (!['roadmap', 'satellite', 'hybrid'].includes(mode)) {
                return;
            }

            this.mapViewMode = mode;

            if (this.map) {
                this.map.setMapTypeId(mode);
            }
        },
        initializeDrawingManager() {
            this.drawingManager = new window.google.maps.drawing.DrawingManager({
                drawingMode: null,
                drawingControl: false,
                markerOptions: { draggable: true },
                polygonOptions: {
                    clickable: true,
                    editable: true,
                    draggable: false,
                    fillColor: '#0f62fe',
                    fillOpacity: 0.18,
                    strokeColor: '#0f62fe',
                    strokeOpacity: 0.92,
                    strokeWeight: 2,
                },
            });

            this.drawingManager.setMap(this.map);
            this.drawingManager.addListener('overlaycomplete', event => {
                if (event.type !== window.google.maps.drawing.OverlayType.POLYGON) {
                    return;
                }

                this.replaceEditingPolygon(event.overlay);
                this.drawingManager.setDrawingMode(null);
                this.isDrawingPolygon = false;
                this.toastSuccess('polygon_ready_to_save', 'Polygon ready to save.');
            });
        },
        async loadLocations(selectId = null) {
            const response = await this.axiosGet(actions.LOCATION);
            const payload = Array.isArray(response.data) ? response.data : (response.data.data || []);

            this.locations = payload.map(location => this.normalizeLocation(location));

            if (selectId) {
                const matched = this.locations.find(location => `${location.id}` === `${selectId}`);
                this.selectedLocationId = matched ? matched.id : this.selectedLocationId;
            } else if (this.selectedLocationId) {
                const stillExists = this.locations.find(location => `${location.id}` === `${this.selectedLocationId}`);
                this.selectedLocationId = stillExists ? stillExists.id : null;
            } else if (this.locations.length) {
                this.selectedLocationId = this.locations[0].id;
            }

            this.renderMapLayers();

            if (this.selectedLocation) {
                this.focusLocationOnMap(this.selectedLocation, false);
            }
        },
        async reloadLocations() {
            this.isLoading = true;

            try {
                await this.loadLocations(this.selectedLocationId);
                this.toastSuccess('locations_reloaded', 'Locations reloaded.');
            } catch (error) {
                this.toastError('unable_to_reload_locations', 'Unable to reload locations.');
            } finally {
                this.isLoading = false;
            }
        },
        normalizeLocation(location) {
            const normalizedGeojson = this.normalizeGeojson(location.geojson);
            const derivedCentroid = normalizedGeojson ? this.centroidFromGeoJson(normalizedGeojson) : null;
            const centerLat = this.toNumber(location.center_lat ?? location.latitude ?? location.lat ?? derivedCentroid?.lat);
            const centerLng = this.toNumber(location.center_lng ?? location.longitude ?? location.lng ?? derivedCentroid?.lng);
            const radius = this.toInteger(location.radius_m ?? location.radius ?? DEFAULT_RADIUS, DEFAULT_RADIUS);
            const isActive = typeof location.is_active === 'boolean'
                ? location.is_active
                : !['0', 0, false, null].includes(location.is_active);
            const geofenceType = location.geofence_type || (normalizedGeojson ? 'polygon' : 'radius');

            return {
                id: location.id,
                name: location.name || this.trans('untitled_location', 'Untitled location'),
                code: location.code || '',
                address: location.address || '',
                description: location.description || '',
                time_zone: location.time_zone || '',
                geofence_type: geofenceType,
                center_lat: centerLat,
                center_lng: centerLng,
                radius_m: radius,
                latitude: centerLat,
                longitude: centerLng,
                lat: centerLat,
                lng: centerLng,
                radius,
                geojson: normalizedGeojson,
                gps_tolerance_m: this.toInteger(location.gps_tolerance_m, 15),
                allowed_clockin_distance_m: this.toInteger(location.allowed_clockin_distance_m, 50),
                offline_attendance_allowed: typeof location.offline_attendance_allowed === 'boolean'
                    ? location.offline_attendance_allowed
                    : !['0', 0, false, null].includes(location.offline_attendance_allowed),
                is_active: isActive,
            };
        },
        normalizeGeojson(geojson) {
            if (!geojson) {
                return null;
            }

            if (typeof geojson === 'string') {
                try {
                    return JSON.parse(geojson);
                } catch (error) {
                    return null;
                }
            }

            return geojson;
        },
        toNumber(value, fallback = null) {
            const parsed = Number.parseFloat(value);
            return Number.isFinite(parsed) ? parsed : fallback;
        },
        toInteger(value, fallback = 0) {
            const parsed = Number.parseInt(value, 10);
            return Number.isFinite(parsed) ? parsed : fallback;
        },
        renderMapLayers() {
            if (!this.isMapReady) {
                return;
            }

            this.clearMapLayers();

            this.visibleLocations.forEach(location => {
                this.locationLayers[location.id] = this.createLocationLayers(location);
            });
        },
        clearMapLayers() {
            Object.values(this.locationLayers).forEach(layer => {
                if (layer.marker) {
                    layer.marker.setMap(null);
                }
                if (layer.geofence) {
                    layer.geofence.setMap(null);
                }
            });

            this.locationLayers = {};
        },
        createLocationLayers(location) {
            if (location.center_lat === null || location.center_lng === null) {
                return {};
            }

            const isSelected = this.selectedLocation && `${this.selectedLocation.id}` === `${location.id}`;
            const marker = createWorksiteMarkerOverlay({
                google: window.google,
                map: this.map,
                location,
                isSelected,
                onClick: selectedLocation => {
                    this.selectLocation(selectedLocation);
                },
            });

            const geofence = this.createGeofenceOverlay(location, isSelected);

            return { marker, geofence };
        },
        createGeofenceOverlay(location, isSelected) {
            const palette = this.resolveGeofencePalette(location, isSelected);

            if (location.geofence_type === 'polygon' && location.geojson) {
                return new window.google.maps.Polygon({
                    map: this.map,
                    paths: this.geoJsonToPath(location.geojson),
                    clickable: false,
                    editable: false,
                    fillColor: palette.fillColor,
                    fillOpacity: palette.fillOpacity,
                    strokeColor: palette.strokeColor,
                    strokeOpacity: 0.95,
                    strokeWeight: palette.strokeWeight,
                });
            }

            return new window.google.maps.Circle({
                map: this.map,
                center: { lat: location.center_lat, lng: location.center_lng },
                radius: location.radius_m,
                fillColor: palette.fillColor,
                fillOpacity: palette.fillOpacity,
                strokeColor: palette.strokeColor,
                strokeOpacity: 0.95,
                strokeWeight: palette.strokeWeight,
            });
        },
        resolveGeofencePalette(location, isSelected) {
            const baseColor = location.is_active ? ACTIVE_LOCATION_COLOR : INACTIVE_LOCATION_COLOR;

            if (isSelected) {
                return {
                    strokeColor: baseColor,
                    fillColor: baseColor,
                    fillOpacity: 0.2,
                    strokeWeight: 3,
                };
            }

            return {
                strokeColor: baseColor,
                fillColor: baseColor,
                fillOpacity: location.is_active ? 0.12 : 0.08,
                strokeWeight: 2,
            };
        },
        buildMarkerIcon(location, isSelected) {
            const fill = isSelected ? '#0f62fe' : (location.is_active ? '#1f7a8c' : '#7b8794');

            return {
                path: window.google.maps.SymbolPath.CIRCLE,
                fillColor: fill,
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 2,
                scale: isSelected ? 9 : 7,
            };
        },
        selectLocation(location) {
            this.selectedLocationId = location.id;
            this.renderMapLayers();
            this.focusLocationOnMap(location);
        },
        focusLocationOnMap(location, smoothZoom = true) {
            if (!this.map) {
                return;
            }

            const layer = this.locationLayers[location.id];
            const bounds = new window.google.maps.LatLngBounds();

            if (location.geofence_type === 'polygon' && location.geojson) {
                this.geoJsonToPath(location.geojson).forEach(point => bounds.extend(point));
                if (!bounds.isEmpty()) {
                    this.fitMapToBounds(bounds, FOCUS_PADDING, smoothZoom);
                    return;
                }
            }

            if (layer?.geofence && typeof layer.geofence.getBounds === 'function') {
                const geofenceBounds = layer.geofence.getBounds();

                if (geofenceBounds && !geofenceBounds.isEmpty()) {
                    this.fitMapToBounds(geofenceBounds, FOCUS_PADDING, smoothZoom);
                } else if (location.center_lat !== null && location.center_lng !== null) {
                    bounds.extend({ lat: location.center_lat, lng: location.center_lng });
                    this.fitMapToBounds(bounds, FOCUS_PADDING + 24, smoothZoom);
                }
            } else if (location.center_lat !== null && location.center_lng !== null) {
                bounds.extend({ lat: location.center_lat, lng: location.center_lng });
                this.fitMapToBounds(bounds, FOCUS_PADDING + 24, smoothZoom);
            }

            if (smoothZoom && location.center_lat !== null && location.center_lng !== null) {
                window.setTimeout(() => {
                    this.map.panTo({ lat: location.center_lat, lng: location.center_lng });
                }, 140);
            }

        },
        fitMapToBounds(bounds, padding = FOCUS_PADDING, smoothZoom = true) {
            if (!this.map || !bounds || bounds.isEmpty()) {
                return;
            }

            this.map.fitBounds(bounds, padding);

            window.google.maps.event.addListenerOnce(this.map, 'idle', () => {
                if (this.map.getZoom() > MAX_FOCUS_ZOOM) {
                    this.map.setZoom(MAX_FOCUS_ZOOM);
                }

                if (!smoothZoom) {
                    return;
                }

                const center = bounds.getCenter();
                if (center) {
                    this.map.panTo(center);
                }
            });
        },
        openCreateModal(preset = {}) {
            this.form = this.defaultForm();
            this.isDrawingPolygon = false;
            const center = this.map ? this.map.getCenter() : null;

            this.form.center_lat = preset.center_lat ?? (center ? Number(center.lat().toFixed(7)) : DEFAULT_CENTER.lat);
            this.form.center_lng = preset.center_lng ?? (center ? Number(center.lng().toFixed(7)) : DEFAULT_CENTER.lng);
            this.form.address = preset.address || '';
            this.openModal();
        },
        openEditModal(location) {
            if (!location) {
                return;
            }

            this.isDrawingPolygon = false;
            this.form = {
                id: location.id,
                name: location.name,
                code: location.code,
                address: location.address,
                description: location.description,
                time_zone: location.time_zone,
                geofence_type: location.geofence_type,
                center_lat: location.center_lat,
                center_lng: location.center_lng,
                radius_m: location.radius_m,
                geojson: location.geojson ? JSON.parse(JSON.stringify(location.geojson)) : null,
                gps_tolerance_m: location.gps_tolerance_m,
                allowed_clockin_distance_m: location.allowed_clockin_distance_m,
                offline_attendance_allowed: location.offline_attendance_allowed,
                is_active: location.is_active,
            };

            this.selectedLocationId = location.id;
            this.openModal();
        },
        async openModal() {
            if (!this.timeZones.length) {
                await this.getTimeZones();
            }

            this.isEditorCollapsed = false;
            this.isModalActive = true;
            this.$nextTick(() => {
                this.prepareEditorGeofence();
                this.refreshMapAfterEditorLayoutChange();
            });
        },
        closeModal() {
            this.isModalActive = false;
            this.isEditorCollapsed = false;
            this.isDrawingPolygon = false;
            this.clearEditingOverlays();
            this.pendingDeleteLocationId = null;
        },
        toggleEditorCollapse() {
            this.isEditorCollapsed = !this.isEditorCollapsed;

            this.$nextTick(() => {
                this.refreshMapAfterEditorLayoutChange();
            });
        },
        refreshMapAfterEditorLayoutChange() {
            if (!this.map || !window.google?.maps) {
                return;
            }

            window.google.maps.event.trigger(this.map, 'resize');

            if (this.isModalActive) {
                if (this.form.geofence_type === 'polygon' && this.editingPolygon) {
                    const bounds = new window.google.maps.LatLngBounds();
                    this.editingPolygon.getPath().forEach(point => bounds.extend(point));

                    if (!bounds.isEmpty()) {
                        this.fitMapToBounds(bounds, FOCUS_PADDING, false);
                        return;
                    }
                }

                if (this.form.center_lat !== null && this.form.center_lng !== null) {
                    this.map.panTo({
                        lat: this.form.center_lat,
                        lng: this.form.center_lng,
                    });
                }

                return;
            }

            if (this.selectedLocation) {
                this.focusLocationOnMap(this.selectedLocation, false);
            }
        },
        async resolveAddressFromCoordinates(latLng) {
            if (!this.geocoder || !latLng) {
                return '';
            }

            try {
                const response = await this.geocoder.geocode({ location: latLng });
                const results = response?.results || [];
                return results[0]?.formatted_address || '';
            } catch (error) {
                return '';
            }
        },
        prepareEditorGeofence() {
            if (!this.isMapReady) {
                return;
            }

            if (this.form.geofence_type === 'polygon') {
                this.setMapViewMode('hybrid');
                this.mountEditingPolygon();
            } else {
                this.syncRadiusPreview();
            }
        },
        handleMapClick(event) {
            if (!event?.latLng) {
                return;
            }

            if (!this.isModalActive) {
                this.openCreateModalFromMapClick(event.latLng);
                return;
            }

            if (this.form.geofence_type !== 'radius') {
                return;
            }

            this.form.center_lat = Number(event.latLng.lat().toFixed(7));
            this.form.center_lng = Number(event.latLng.lng().toFixed(7));
            this.syncRadiusPreview();
        },
        async openCreateModalFromMapClick(latLng) {
            this.openCreateModal({
                center_lat: Number(latLng.lat().toFixed(7)),
                center_lng: Number(latLng.lng().toFixed(7)),
            });

            this.setPlaceSearchMarker(latLng, this.trans('new_location_pin', 'New location'));
            // this.$toastr.i(this.trans('map_click_location_hint', 'Location form opened from the selected map point.'));

            const token = ++this.prefillRequestToken;
            const resolvedAddress = await this.resolveAddressFromCoordinates(latLng);

            if (token === this.prefillRequestToken && this.isModalActive && !this.isEditing && !this.form.address) {
                this.form.address = resolvedAddress || '';
            }
        },
        setGeofenceType(type) {
            if (this.form.geofence_type === type) {
                return;
            }

            this.form.geofence_type = type;
        },
        handleGeofenceModeChange(type) {
            if (type === 'polygon') {
                this.setMapViewMode('hybrid');
                this.clearRadiusPreview();
                this.mountEditingPolygon();
                return;
            }

            this.clearPolygonDrawing();
            this.ensureRadiusCenter();
            this.syncRadiusPreview();
        },
        ensureRadiusCenter() {
            if (this.form.center_lat !== null && this.form.center_lng !== null) {
                return;
            }

            const center = this.map ? this.map.getCenter() : null;
            this.form.center_lat = center ? Number(center.lat().toFixed(7)) : DEFAULT_CENTER.lat;
            this.form.center_lng = center ? Number(center.lng().toFixed(7)) : DEFAULT_CENTER.lng;
        },
        syncRadiusPreview() {
            this.ensureRadiusCenter();

            if (this.form.center_lat === null || this.form.center_lng === null) {
                return;
            }

            const center = {
                lat: this.form.center_lat,
                lng: this.form.center_lng,
            };

            if (!this.editingMarker) {
                this.editingMarker = new window.google.maps.Marker({
                    position: center,
                    map: this.map,
                    draggable: true,
                    icon: this.buildMarkerIcon({ is_active: true }, true),
                });

                this.editingMarker.addListener('dragend', event => {
                    this.form.center_lat = Number(event.latLng.lat().toFixed(7));
                    this.form.center_lng = Number(event.latLng.lng().toFixed(7));
                    this.syncRadiusPreview();
                });
            } else {
                this.editingMarker.setMap(this.map);
                this.editingMarker.setPosition(center);
            }

            if (!this.editingCircle) {
                this.editingCircle = new window.google.maps.Circle({
                    map: this.map,
                    center,
                    radius: this.form.radius_m || DEFAULT_RADIUS,
                    fillColor: '#0f62fe',
                    fillOpacity: 0.18,
                    strokeColor: '#0f62fe',
                    strokeOpacity: 0.95,
                    strokeWeight: 2,
                });
            } else {
                this.editingCircle.setMap(this.map);
                this.editingCircle.setCenter(center);
                this.editingCircle.setRadius(this.form.radius_m || DEFAULT_RADIUS);
            }

            this.map.panTo(center);
        },
        applyManualCoordinates() {
            const lat = this.toNumber(this.form.center_lat, null);
            const lng = this.toNumber(this.form.center_lng, null);

            this.form.center_lat = lat;
            this.form.center_lng = lng;

            if (lat === null || lng === null || !this.map) {
                return;
            }

            const center = { lat, lng };

            if (this.form.geofence_type === 'radius') {
                this.syncRadiusPreview();
                return;
            }

            this.map.panTo(center);

            if (this.map.getZoom() < 15) {
                this.map.setZoom(15);
            }
        },
        clearRadiusPreview() {
            if (this.editingMarker) {
                this.editingMarker.setMap(null);
                this.editingMarker = null;
            }

            if (this.editingCircle) {
                this.editingCircle.setMap(null);
                this.editingCircle = null;
            }
        },
        beginPolygonDrawing() {
            if (!this.drawingManager) {
                return;
            }

            this.clearPolygonDrawing();
            this.isDrawingPolygon = true;
            this.drawingManager.setDrawingMode(window.google.maps.drawing.OverlayType.POLYGON);
            this.toastInfo('draw_polygon_on_map', 'Click points on the map, then double-click to finish the polygon.');
        },
        mountEditingPolygon() {
            this.clearPolygonDrawing();

            if (!this.form.geojson) {
                return;
            }

            const polygon = new window.google.maps.Polygon({
                map: this.map,
                paths: this.geoJsonToPath(this.form.geojson),
                editable: true,
                draggable: false,
                fillColor: '#0f62fe',
                fillOpacity: 0.18,
                strokeColor: '#0f62fe',
                strokeOpacity: 0.95,
                strokeWeight: 2,
            });

            this.replaceEditingPolygon(polygon);
        },
        replaceEditingPolygon(polygon) {
            this.clearPolygonDrawing();
            this.editingPolygon = polygon;
            this.isDrawingPolygon = false;
            this.bindPolygonListeners(polygon);
            this.syncFormWithPolygon(polygon);

            const bounds = new window.google.maps.LatLngBounds();
            polygon.getPath().forEach(point => bounds.extend(point));
            if (!bounds.isEmpty()) {
                this.map.fitBounds(bounds, 100);
            }
        },
        bindPolygonListeners(polygon) {
            const path = polygon.getPath();
            ['insert_at', 'remove_at', 'set_at'].forEach(eventName => {
                path.addListener(eventName, () => this.syncFormWithPolygon(polygon));
            });
        },
        syncFormWithPolygon(polygon) {
            const geojson = this.polygonToGeoJson(polygon);
            const centroid = this.centroidFromGeoJson(geojson);

            this.form.geojson = geojson;
            this.form.center_lat = centroid ? centroid.lat : null;
            this.form.center_lng = centroid ? centroid.lng : null;
        },
        clearPolygonDrawing() {
            this.isDrawingPolygon = false;

            if (this.editingPolygon) {
                this.editingPolygon.setMap(null);
                this.editingPolygon = null;
            }

            if (this.drawingManager) {
                this.drawingManager.setDrawingMode(null);
            }

            if (this.form.geofence_type === 'polygon') {
                this.form.geojson = null;
            }
        },
        clearEditingOverlays() {
            this.clearRadiusPreview();

            if (this.editingPolygon) {
                this.editingPolygon.setMap(null);
                this.editingPolygon = null;
            }

            if (this.drawingManager) {
                this.drawingManager.setDrawingMode(null);
            }
        },
        geoJsonToPath(geojson) {
            if (!geojson) {
                return [];
            }

            const geometry = geojson.type === 'Feature' ? geojson.geometry : geojson;
            const ring = geometry?.coordinates?.[0] || [];

            return ring
                .filter(point => Array.isArray(point) && point.length >= 2)
                .map(point => ({ lat: Number(point[1]), lng: Number(point[0]) }));
        },
        polygonToGeoJson(polygon) {
            const coordinates = [];
            const path = polygon.getPath();

            path.forEach(point => {
                coordinates.push([
                    Number(point.lng().toFixed(7)),
                    Number(point.lat().toFixed(7)),
                ]);
            });

            if (coordinates.length) {
                const [firstLng, firstLat] = coordinates[0];
                const [lastLng, lastLat] = coordinates[coordinates.length - 1];

                if (firstLng !== lastLng || firstLat !== lastLat) {
                    coordinates.push([firstLng, firstLat]);
                }
            }

            return {
                type: 'Polygon',
                coordinates: [coordinates],
            };
        },
        centroidFromGeoJson(geojson) {
            const path = this.geoJsonToPath(geojson);

            if (!path.length) {
                return null;
            }

            const lat = path.reduce((total, point) => total + point.lat, 0) / path.length;
            const lng = path.reduce((total, point) => total + point.lng, 0) / path.length;

            return {
                lat: Number(lat.toFixed(7)),
                lng: Number(lng.toFixed(7)),
            };
        },
        polygonPointCount(geojson) {
            const path = this.geoJsonToPath(geojson);

            if (!path.length) {
                return 0;
            }

            if (path.length > 1) {
                const first = path[0];
                const last = path[path.length - 1];
                if (first.lat === last.lat && first.lng === last.lng) {
                    return path.length - 1;
                }
            }

            return path.length;
        },
        escapeHtml(value) {
            return String(value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
        },
        displayCoordinate(value) {
            return value === null || value === undefined ? '--' : Number(value).toFixed(6);
        },
        buildPayload() {
            const payload = {
                name: this.form.name,
                code: this.form.code || null,
                address: this.form.address || null,
                description: this.form.description || null,
                time_zone: this.form.time_zone || null,
                geofence_type: this.form.geofence_type,
                gps_tolerance_m: this.toInteger(this.form.gps_tolerance_m, 15),
                allowed_clockin_distance_m: this.toInteger(this.form.allowed_clockin_distance_m, 15),
                offline_attendance_allowed: !!this.form.offline_attendance_allowed,
                is_active: !!this.form.is_active,
            };

            if (this.form.geofence_type === 'polygon') {
                payload.geojson = this.form.geojson;
                if (this.form.center_lat !== null && this.form.center_lng !== null) {
                    payload.center_lat = this.form.center_lat;
                    payload.center_lng = this.form.center_lng;
                }
            } else {
                payload.center_lat = this.form.center_lat;
                payload.center_lng = this.form.center_lng;
                payload.radius_m = this.toInteger(this.form.radius_m, DEFAULT_RADIUS);
            }

            return payload;
        },
        validateForm() {
            if (!this.form.name.trim()) {
                this.toastError('location_name_required', 'Location name is required.');
                return false;
            }

            if (this.form.geofence_type === 'radius') {
                if (this.form.center_lat === null || this.form.center_lng === null) {
                    this.toastError('choose_radius_center', 'Choose a center on the map for this radius.');
                    return false;
                }

                if (!this.form.radius_m || this.form.radius_m < 1) {
                    this.toastError('radius_must_be_positive', 'Radius must be greater than zero.');
                    return false;
                }
            }

            if (this.form.geofence_type === 'polygon' && !this.form.geojson) {
                this.toastError('draw_polygon_before_save', 'Draw a polygon before saving the location.');
                return false;
            }

            return true;
        },
        async saveLocation() {
            if (!this.validateForm()) {
                return;
            }

            this.isSaving = true;
            const payload = this.buildPayload();

            try {
                const response = this.isEditing
                    ? await this.axiosPut({ url: `${actions.LOCATION}/${this.form.id}`, data: payload })
                    : await this.axiosPost({ url: actions.LOCATION, data: payload });

                const savedLocation = response?.data?.location;
                this.toastSuccess('location_saved', 'Location saved.');
                this.closeModal();
                await this.loadLocations(savedLocation?.id || this.selectedLocationId);
            } catch (error) {
                this.toastError('unable_to_save_location', 'Unable to save location.');
            } finally {
                this.isSaving = false;
            }
        },
        openDeleteConfirmation(location) {
            const target = location || this.selectedLocation;

            if (!target) {
                return;
            }

            this.pendingDeleteLocationId = target.id;
            this.deleteConfirmationModalActive = true;
        },
        async confirmedDelete() {
            if (!this.pendingDeleteLocationId) {
                return;
            }

            this.deleteLoader = true;

            try {
                await this.axiosDelete(`${actions.LOCATION}/${this.pendingDeleteLocationId}`);
                this.toastSuccess('location_deleted', 'Location deleted.');

                if (`${this.selectedLocationId}` === `${this.pendingDeleteLocationId}`) {
                    this.selectedLocationId = null;
                }

                await this.loadLocations();
                this.cancelDelete();
            } catch (error) {
                this.toastError('unable_to_delete_location', 'Unable to delete location.');
            } finally {
                this.deleteLoader = false;
            }
        },
        cancelDelete() {
            window.$('#location-delete').modal('hide');
            this.pendingDeleteLocationId = null;
            this.deleteConfirmationModalActive = false;
            this.deleteLoader = false;
        },
        async getTimeZones() {
            try {
                const response = await this.axiosGet(actions.GENERAL_SETTINGS);
                this.timeZones = response?.data?.time_zones || [];
            } catch (error) {
                this.timeZones = [];
            }
        },
    },
};
</script>

<style scoped>
.location-worksites-page {
    min-height: calc(100vh - 64px);
    padding: 1rem;
    background:
        radial-gradient(circle at top left, rgba(15, 98, 254, 0.08), transparent 30%),
        var(--mozali-page-soft-bg);
}

.worksites-shell {
    display: grid;
    grid-template-columns: minmax(300px, 332px) minmax(0, 1fr);
    gap: 1rem;
    min-height: calc(100vh - 96px);
}

.worksites-shell.is-editor-open {
    display: block;
    min-height: 0;
}

.card {
    background: var(--mozali-surface-raised);
    border: 1px solid var(--mozali-border-soft);
    border-radius: 20px;
    box-shadow: 0 14px 34px rgba(15, 23, 42, 0.07);
}

.worksites-sidebar {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-height: 0;
}

.worksites-sidebar.is-editor-open {
    display: none;
}

.sidebar-hero,
.sidebar-filters,
.sidebar-list,
.selected-card,
.worksites-stage,
.editor-section {
    padding: 1rem;
}

.sidebar-hero {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    color: var(--default-font-color);
    background: linear-gradient(145deg, var(--mozali-surface-overlay-strong) 0%, var(--mozali-surface-muted) 100%);
    border: 1px solid rgba(15, 98, 254, 0.08);
    border-radius: 20px;
    box-shadow: 0 14px 34px rgba(15, 23, 42, 0.07);
}

.sidebar-kicker,
.toolbar-kicker,
.detail-kicker {
    margin-bottom: 0.35rem;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--mozali-color-brand);
}

.sidebar-hero h2,
.worksites-stage h3,
.selected-card h4,
.editor-section h5 {
    margin-bottom: 0.35rem;
    font-weight: 700;
    color: var(--default-font-color);
}

.sidebar-copy,
.selected-address,
.polygon-copy {
    margin-bottom: 0;
    color: var(--sidebar-text-muted);
    line-height: 1.55;
}

.sidebar-add-btn {
    align-self: flex-start;
}

.sidebar-filters .filter-row + .filter-row {
    margin-top: 1rem;
}

.filter-label {
    display: block;
    margin-bottom: 0.45rem;
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--default-font-color);
}

.filter-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 1rem;
}

.filter-pill {
    padding: 0.4rem 0.75rem;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 600;
}

.active-pill {
    background: rgba(22, 163, 74, 0.12);
    color: #15803d;
}

.inactive-pill {
    background: rgba(220, 38, 38, 0.1);
    color: #b91c1c;
}

.neutral-pill {
    background: rgba(15, 98, 254, 0.09);
    color: #0f62fe;
}

.sidebar-list {
    display: flex;
    flex-direction: column;
    min-height: 280px;
    max-height: 40vh;
    min-height: 0;
}

.sidebar-list-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding-right: 0.3rem;
    margin-right: -0.3rem;
    overscroll-behavior: contain;
    scrollbar-width: thin;
}

.sidebar-list-body::-webkit-scrollbar {
    width: 6px;
}

.sidebar-list-body::-webkit-scrollbar-thumb {
    background: rgba(123, 135, 148, 0.35);
    border-radius: 999px;
}

.sidebar-list-body::-webkit-scrollbar-track {
    background: transparent;
}

.sidebar-list-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
}

.sidebar-list-head h5 {
    margin-bottom: 0;
}

.sidebar-list-head span {
    font-size: 0.82rem;
    color: var(--sidebar-text-muted);
}

.location-list-item {
    width: 100%;
    text-align: left;
    border: 0;
    background: var(--default-card-bg);
    border-radius: 16px;
    padding: 0.85rem 0.9rem;
    margin-bottom: 0.65rem;
    box-shadow: inset 0 0 0 1px var(--mozali-border-soft);
    transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.location-list-item:hover,
.location-list-item.is-selected {
    transform: translateY(-1px);
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.09);
}

.location-list-item.is-selected {
    box-shadow: inset 0 0 0 2px rgba(15, 98, 254, 0.35), 0 12px 26px rgba(15, 23, 42, 0.08);
}

.location-item-main {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
}

.location-item-title-row {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    margin-bottom: 0.25rem;
}

.location-item-title-row h6 {
    margin-bottom: 0;
    font-weight: 700;
    color: var(--default-font-color);
}

.location-item-main p,
.location-item-meta {
    margin-bottom: 0;
    color: var(--sidebar-text-muted);
    font-size: 0.82rem;
}

.location-item-meta {
    display: flex;
    justify-content: space-between;
    margin-top: 0.65rem;
}

.status-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
}

.dot-active {
    background: #16a34a;
}

.dot-inactive {
    background: #dc2626;
}

.geofence-chip,
.editor-badge,
.selected-status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.3rem 0.7rem;
    border-radius: 999px;
    font-size: 0.74rem;
    font-weight: 700;
}

.geofence-chip.type-radius,
.editor-badge,
.selected-status.is-active {
    background: rgba(15, 98, 254, 0.1);
    color: #0f62fe;
}

.geofence-chip.type-polygon {
    background: rgba(31, 122, 140, 0.12);
    color: #1f7a8c;
}

.selected-status.is-inactive {
    background: rgba(123, 135, 148, 0.16);
    color: #52606d;
}

.selected-grid,
.settings-grid,
.mini-grid,
.editor-grid {
    display: grid;
    gap: 1rem;
}

.selected-grid,
.settings-grid,
.mini-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.selected-label {
    display: block;
    margin-bottom: 0.2rem;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--sidebar-text-muted);
}

.coordinates-card {
    margin-bottom: 1rem;
    padding: 0.95rem 1rem;
    border: 1px solid rgba(15, 98, 254, 0.08);
    border-radius: 16px;
    background: linear-gradient(145deg, var(--mozali-surface-overlay-strong) 0%, var(--mozali-surface-muted) 100%);
}

.coordinates-card__head {
    margin-bottom: 0.85rem;
}

.coordinates-card__head strong {
    display: block;
    margin-bottom: 0.25rem;
    color: var(--default-font-color);
}

.coordinates-card__head p {
    margin-bottom: 0;
    color: var(--sidebar-text-muted);
    line-height: 1.5;
}

.selected-actions,
.toolbar-actions,
.polygon-toolbar,
.type-toggle,
.radius-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.map-mode-toggle {
    gap: 0.45rem;
}

.map-mode-btn {
    min-width: 112px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    padding: 0.5rem 0.8rem;
    border: 1px solid var(--mozali-border-soft);
    border-radius: 12px;
    background: var(--mozali-surface-overlay);
    color: var(--default-font-color);
    font-weight: 600;
    backdrop-filter: blur(8px);
    box-shadow: none;
    transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease, transform 0.18s ease;
}

.map-mode-btn:hover,
.map-mode-btn:focus {
    background: var(--mozali-surface-overlay-strong);
    border-color: rgba(59, 130, 246, 0.22);
    color: var(--default-font-color);
}

.map-mode-btn.is-active {
    background: rgba(15, 98, 254, 0.2);
    border-color: rgba(15, 98, 254, 0.28);
    color: #0f62fe;
}

.map-mode-btn-icon {
    opacity: 0.86;
}

.selected-actions {
    margin-top: 1rem;
}

.worksites-stage {
    display: flex;
    flex-direction: column;
    min-height: 100%;
}

.worksites-stage.is-editor-open {
    position: fixed;
    inset: 0;
    z-index: 1070;
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    padding: 1rem calc(430px + 2rem) 1rem 1rem;
    margin: 0;
    border-radius: 0;
    border: 0;
    box-shadow: none;
    background: var(--mozali-page-soft-bg);
}

.worksites-stage.is-editor-open.is-editor-collapsed {
    padding-right: calc(84px + 1rem);
}

.worksites-stage.is-editor-open .stage-map-wrap {
    min-height: 0;
    height: 100%;
    border-radius: 22px;
}

.worksites-stage.is-editor-open .worksites-map {
    min-height: 100%;
    height: 100%;
}

.stage-toolbar,
.editor-section-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.85rem;
}

.stage-map-wrap {
    position: relative;
    flex: 1;
    min-height: clamp(460px, 58vh, 560px);
    overflow: hidden;
    border-radius: 18px;
    background: var(--mozali-surface-muted);
}

.worksites-map {
    width: 100%;
    height: 100%;
    min-height: clamp(460px, 58vh, 560px);
}

.map-overlay {
    position: absolute;
    z-index: 2;
    background: var(--mozali-surface-overlay);
    backdrop-filter: blur(8px);
    border: 1px solid var(--mozali-border-soft);
    box-shadow: 0 14px 30px rgba(15, 23, 42, 0.12);
}

.editor-status-overlay {
    top: 1rem;
    left: 50%;
    transform: translateX(-50%);
    width: min(640px, calc(100% - 14rem));
    padding: 0.85rem 1rem;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.editor-status-copy {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
}

.editor-status-copy strong {
    color: var(--default-font-color);
    font-size: 0.98rem;
}

.editor-status-copy span {
    color: var(--sidebar-text-muted);
    font-size: 0.82rem;
    line-height: 1.45;
}

.editor-status-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.55rem;
    flex-wrap: wrap;
}

.legend-overlay {
    top: 1rem;
    right: 1rem;
    padding: 0.85rem 1rem;
    border-radius: 16px;
}

.map-mode-overlay {
    top: 1rem;
    left: 1rem;
    padding: 0.4rem;
    border-radius: 18px;
    background: var(--mozali-surface-overlay);
    border-color: var(--mozali-border-soft);
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.1);
}

.edit-hint-overlay {
    left: 50%;
    bottom: 1rem;
    transform: translateX(-50%);
    max-width: 480px;
    padding: 0.85rem 1rem;
    border-radius: 999px;
    font-weight: 600;
    color: var(--default-font-color);
}

.geofence-studio-overlay {
    left: 1rem;
    bottom: 1rem;
    width: min(390px, calc(100% - 2rem));
    padding: 1rem;
    border-radius: 20px;
    background: var(--mozali-surface-overlay);
    backdrop-filter: blur(10px);
}

.studio-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.85rem;
}

.studio-header h5 {
    color: var(--default-font-color);
    font-weight: 700;
}

.studio-panel {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.studio-coordinates {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
}

.studio-coordinates div {
    padding: 0.75rem 0.85rem;
    border-radius: 14px;
    background: var(--mozali-surface-muted);
    border: 1px solid var(--mozali-border-soft);
}

.studio-coordinates span {
    display: block;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--sidebar-text-muted);
    margin-bottom: 0.25rem;
}

.studio-coordinates strong {
    color: var(--default-font-color);
    font-size: 0.92rem;
}

.studio-copy,
.map-studio-summary p {
    margin: 0;
    color: var(--sidebar-text-muted);
    font-size: 0.84rem;
    line-height: 1.5;
}

.geofence-overlay-toggle {
    margin-bottom: 0.85rem;
}

.map-studio-summary {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
    padding: 0.95rem 1rem;
    border-radius: 16px;
    background: linear-gradient(180deg, var(--mozali-surface-overlay-strong), var(--mozali-surface-muted));
    border: 1px solid var(--mozali-border-soft);
    margin-bottom: 1rem;
}

.map-studio-summary strong {
    color: var(--default-font-color);
}

.polygon-guidance.compact {
    padding: 0.8rem 0.9rem;
    border-radius: 14px;
}

.polygon-summary.compact {
    margin-top: 0;
}

.legend-row {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    font-size: 0.82rem;
    color: var(--default-font-color);
}

.legend-row + .legend-row {
    margin-top: 0.4rem;
}

.legend-swatch {
    width: 12px;
    height: 12px;
    border-radius: 999px;
}

.swatch-active {
    background: #16a34a;
}

.swatch-inactive {
    background: #dc2626;
}

.swatch-selected {
    background: #0f62fe;
}

.list-state {
    min-height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.empty-state {
    flex-direction: column;
    gap: 0.75rem;
    color: var(--sidebar-text-muted);
    text-align: center;
}

.location-editor {
    position: relative;
}

.editor-drawer-shell {
    position: fixed;
    inset: 0;
    z-index: 1080;
    display: flex;
    justify-content: flex-end;
    pointer-events: none;
}

.location-editor-drawer {
    width: 100vw;
    max-height: 100vh;
    height: 100vh;
    display: flex;
    flex-direction: column;
    padding: 1rem;
    pointer-events: none;
    background: linear-gradient(90deg, rgba(15, 23, 42, 0) 0%, rgba(15, 23, 42, 0) 58%, rgba(15, 23, 42, 0.14) 100%);
}

.drawer-panel {
    width: min(430px, calc(100vw - 6rem));
    height: calc(100vh - 2rem);
    max-height: calc(100vh - 2rem);
    min-height: 0;
    margin-left: auto;
    display: flex;
    flex-direction: column;
    transition: width 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.2s ease, transform 0.2s ease;
}

.drawer-panel.is-collapsed {
    width: 0;
    opacity: 0;
    transform: translateX(24px);
    pointer-events: none;
    overflow: hidden;
}

.drawer-header,
.drawer-footer {
    padding: 0.95rem 1rem;
    border-bottom: 1px solid var(--mozali-border-soft);
    width: 100%;
    margin-left: auto;
    background: var(--mozali-surface-overlay-strong);
    border-left: 1px solid var(--mozali-border-soft);
    border-right: 1px solid var(--mozali-border-soft);
    pointer-events: auto;
}

.drawer-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    border-top-left-radius: 22px;
    border-top-right-radius: 22px;
}

.drawer-header-actions {
    display: flex;
    align-items: center;
    gap: 0.35rem;
}

.drawer-body {
    flex: 1 1 auto;
    min-height: 0;
    padding: 1rem;
    overflow-y: auto;
    width: 100%;
    margin-left: auto;
    background: var(--mozali-surface-overlay-strong);
    border-left: 1px solid var(--mozali-border-soft);
    border-right: 1px solid var(--mozali-border-soft);
    pointer-events: auto;
}

.drawer-footer {
    border-top: 1px solid var(--mozali-border-soft);
    border-bottom: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    border-bottom-left-radius: 22px;
    border-bottom-right-radius: 22px;
}

.drawer-toggle-rail {
    position: absolute;
    top: 50%;
    right: 1rem;
    z-index: 2;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    transform: translateY(-50%);
    pointer-events: auto;
}

.drawer-toggle-btn {
    width: 42px;
    height: 42px;
    border: 1px solid var(--mozali-border-soft);
    border-radius: 14px;
    background: var(--mozali-surface-overlay);
    color: var(--default-font-color);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
    transition: transform 0.18s ease, border-color 0.18s ease, color 0.18s ease, background-color 0.18s ease;
}

.drawer-toggle-btn:hover,
.drawer-toggle-btn:focus {
    outline: none;
    color: var(--mozali-color-brand);
    border-color: rgba(15, 98, 254, 0.24);
    background: var(--mozali-surface-overlay-strong);
    transform: translateY(-1px);
}

.editor-drawer-enter-active,
.editor-drawer-leave-active {
    transition: opacity 0.24s ease;
}

.editor-drawer-enter,
.editor-drawer-leave-to {
    opacity: 0;
}

.editor-drawer-enter-active .drawer-header,
.editor-drawer-enter-active .drawer-body,
.editor-drawer-enter-active .drawer-footer,
.editor-drawer-leave-active .drawer-header,
.editor-drawer-leave-active .drawer-body,
.editor-drawer-leave-active .drawer-footer {
    transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.24s ease;
    will-change: transform, opacity;
}

.editor-drawer-enter .drawer-header,
.editor-drawer-enter .drawer-body,
.editor-drawer-enter .drawer-footer,
.editor-drawer-leave-to .drawer-header,
.editor-drawer-leave-to .drawer-body,
.editor-drawer-leave-to .drawer-footer {
    opacity: 0;
    transform: translateX(56px);
}

.editor-grid {
    grid-template-columns: 1fr;
}

.form-group label {
    font-weight: 600;
    color: var(--default-font-color);
}

.type-toggle {
    background: var(--mozali-surface-muted);
    padding: 0.3rem;
    border-radius: 999px;
}

.type-toggle-btn {
    border: 0;
    background: transparent;
    color: #1e3a8a;
    font-weight: 700;
    padding: 0.45rem 0.85rem;
    border-radius: 999px;
}

.type-toggle-btn.active {
    background: #0f62fe;
    color: #fff;
    box-shadow: 0 10px 20px rgba(15, 98, 254, 0.22);
}

.radius-editor,
.polygon-editor {
    margin-bottom: 1rem;
}

.polygon-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-top: 0.75rem;
    color: var(--sidebar-text-muted);
    font-size: 0.82rem;
}

.polygon-guidance {
    padding: 0.9rem 1rem;
    border-radius: 16px;
    background: linear-gradient(135deg, rgba(15, 98, 254, 0.09), rgba(31, 122, 140, 0.08));
    border: 1px solid rgba(15, 98, 254, 0.12);
}

.polygon-guidance strong {
    display: block;
    margin-bottom: 0.35rem;
    color: var(--default-font-color);
}

.polygon-guidance p {
    margin: 0;
    color: var(--sidebar-text-muted);
    line-height: 1.55;
}

.switch-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.9rem 0;
    border-top: 1px solid var(--mozali-border-soft);
}

.switch-row p {
    margin: 0.2rem 0 0;
    font-size: 0.84rem;
    color: var(--sidebar-text-muted);
}

.toggle-switch {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.4rem 0.7rem 0.4rem 0.45rem;
    border: 1px solid var(--mozali-border-soft);
    border-radius: 999px;
    background: var(--mozali-surface-muted);
    color: var(--default-font-color);
    font-weight: 700;
    transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
}

.toggle-switch:hover,
.toggle-switch:focus {
    outline: none;
    border-color: rgba(15, 98, 254, 0.28);
    box-shadow: 0 0 0 0.18rem rgba(15, 98, 254, 0.12);
}

.toggle-switch.is-on {
    background: rgba(15, 98, 254, 0.08);
    border-color: rgba(15, 98, 254, 0.24);
    color: #0f62fe;
}

.toggle-switch-track {
    position: relative;
    width: 42px;
    height: 24px;
    border-radius: 999px;
    background: rgba(148, 163, 184, 0.55);
    transition: background-color 0.18s ease;
    flex-shrink: 0;
}

.toggle-switch.is-on .toggle-switch-track {
    background: rgba(15, 98, 254, 0.28);
}

.toggle-switch-thumb {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--default-card-bg);
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.18);
    transition: transform 0.18s ease;
}

.toggle-switch.is-on .toggle-switch-thumb {
    transform: translateX(18px);
}

.toggle-switch-text {
    min-width: 72px;
    text-align: left;
}

@media (max-width: 1199px) {
    .worksites-shell,
    .editor-grid {
        grid-template-columns: 1fr;
    }

    .worksites-sidebar {
        order: 2;
    }

    .worksites-stage {
        order: 1;
        min-height: 520px;
    }

    .editor-status-overlay {
        width: calc(100% - 2rem);
    }

    .worksites-stage.is-editor-open {
        padding: 1rem;
    }
}

@media (max-width: 767px) {
    .location-worksites-page {
        padding: 1rem;
    }

    .editor-drawer-shell {
        inset: 0;
    }

    .location-editor-drawer {
        padding: 0;
        background: transparent;
    }

    .drawer-toggle-rail {
        top: 0.75rem;
        right: 0.75rem;
        transform: none;
    }

    .drawer-panel {
        width: 100vw;
        height: 100vh;
        max-height: 100vh;
    }

    .editor-status-overlay,
    .geofence-studio-overlay {
        left: 1rem;
        right: 1rem;
        width: auto;
        transform: none;
    }

    .editor-status-overlay {
        top: 0.75rem;
        flex-direction: column;
        align-items: stretch;
    }

    .editor-status-actions {
        justify-content: stretch;
    }

    .editor-status-actions .btn {
        width: 100%;
    }

    .geofence-studio-overlay {
        bottom: 0.75rem;
    }

    .worksites-stage.is-editor-open {
        padding: 0.75rem 0.75rem 15.5rem;
    }

    .drawer-panel,
    .drawer-panel.is-collapsed {
        width: 100vw;
        opacity: 1;
        transform: none;
        overflow: visible;
        pointer-events: auto;
    }

    .drawer-header,
    .drawer-body,
    .drawer-footer {
        width: 100%;
        margin-left: 0;
        border-radius: 0;
        border-left: 0;
        border-right: 0;
    }

    .drawer-body {
        max-height: calc(100vh - 13rem);
    }

    .sidebar-hero,
    .sidebar-filters,
    .sidebar-list,
    .selected-card,
    .worksites-stage,
    .editor-section {
        padding: 1rem;
        border-radius: 18px;
    }

    .selected-grid,
    .settings-grid,
    .mini-grid {
        grid-template-columns: 1fr;
    }

    .stage-toolbar,
    .editor-section-head,
    .selected-actions,
    .toolbar-actions {
        flex-direction: column;
        align-items: stretch;
    }

    .map-search-control {
        min-width: 100%;
    }

    .edit-hint-overlay {
        left: 1rem;
        right: 1rem;
        transform: none;
        max-width: none;
        border-radius: 18px;
    }
}
</style>

<style>
.worksite-marker-overlay {
    position: absolute;
    transform: translate(-50%, calc(-100% - 10px));
    pointer-events: none;
}

.worksite-marker-button {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 0.45rem;
    padding: 0;
    border: 0;
    background: transparent;
    pointer-events: auto;
    cursor: pointer;
}

.worksite-marker-button:focus {
    outline: none;
}

.worksite-marker-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.48rem;
    max-width: 220px;
    padding: 0.5rem 0.85rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.98);
    border: 1px solid rgba(15, 23, 42, 0.08);
    box-shadow: 0 16px 36px rgba(15, 23, 42, 0.16);
    color: #172033;
    font-size: 0.82rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    transition: transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease;
}

.worksite-marker-badge-text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.worksite-marker-badge-dot {
    width: 0.62rem;
    height: 0.62rem;
    border-radius: 999px;
    flex-shrink: 0;
    box-shadow: 0 0 0 0.18rem rgba(255, 255, 255, 0.92);
}

.worksite-marker-pin-wrap {
    position: relative;
    width: 22px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.worksite-marker-pin {
    position: relative;
    z-index: 1;
    width: 16px;
    height: 16px;
    border-radius: 999px;
    background: currentColor;
    border: 3px solid rgba(255, 255, 255, 0.96);
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.22);
    transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.worksite-marker-pulse {
    position: absolute;
    inset: 0;
    border-radius: 999px;
    background: currentColor;
    opacity: 0.22;
    animation: worksiteMarkerPulse 2.4s ease-out infinite;
}

.worksite-marker-overlay.is-active {
    color: #16a34a;
}

.worksite-marker-overlay.is-inactive {
    color: #dc2626;
}

.worksite-marker-overlay.is-active .worksite-marker-badge-dot {
    background: #16a34a;
}

.worksite-marker-overlay.is-inactive .worksite-marker-badge-dot {
    background: #dc2626;
}

.worksite-marker-overlay.is-selected .worksite-marker-badge {
    transform: translateY(-1px) scale(1.02);
    box-shadow: 0 20px 42px rgba(15, 23, 42, 0.22);
}

.worksite-marker-overlay.is-selected .worksite-marker-pin {
    transform: scale(1.12);
    box-shadow: 0 14px 28px rgba(15, 23, 42, 0.28);
}

.worksite-marker-button:hover .worksite-marker-badge,
.worksite-marker-button:focus .worksite-marker-badge {
    transform: translateY(-1px);
    box-shadow: 0 18px 38px rgba(15, 23, 42, 0.2);
}

.worksite-marker-button:hover .worksite-marker-pin,
.worksite-marker-button:focus .worksite-marker-pin {
    transform: scale(1.08);
}

@keyframes worksiteMarkerPulse {
    0% {
        transform: scale(0.9);
        opacity: 0.28;
    }
    70% {
        transform: scale(1.85);
        opacity: 0;
    }
    100% {
        transform: scale(1.85);
        opacity: 0;
    }
}
</style>
